import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import StudentFilter from "../../popup/StudentFilter";
import StudentDetails from "../../popup/StudentDetails";
import { useConstants } from "../../hooks/UseConstants";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import { useStudentsFilter } from "../../filter/UseStudentsFilter";
import DeleteDialog from "../../popup/DeleteDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearch } from "../../hooks/UseSearch";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import { usePagination } from "../../hooks/UsePagination";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import AdminListToolbar from "../../components/AdminListToolbar";
import ModalOverlay from "../../components/ModalOverlay";

function formatDate(value) {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value).split("T")[0] || "-";
    return parsed.toISOString().split("T")[0];
}

function Students() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch, order, setOrder } = useSearch();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, filterWait, setFilterWait } = useWaits();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const { majorId, setMajorId, value, setValue, fromCount, setFromCount, toCount, setToCount, fromDate, setFromDate, toDate, setToDate } = useStudentsFilter();
    const [students, setStudents] = useState([]);
    const [studentId, setStudentId] = useState(null);
    const [studentsCount, setStudentsCount] = useState(0);
    const [student, setStudent] = useState(null);

    const studentsUrl = useMemo(() => (
        `${host}/admin/users?account_role=student&page=${page + 1}&search=${search}&direction=asc&${order ? `order_by=${order}` : ""}${fromCount ? `&enrolled_courses[from]=${fromCount}` : ""}${toCount ? `&enrolled_courses[to]=${toCount}` : ""}${fromDate ? `&from=${fromDate}` : ""}${toDate ? `&to=${toDate}` : ""}${majorId ? `&major_id=${majorId}` : ""}`
    ), [host, page, search, order, fromCount, toCount, fromDate, toDate, majorId]);

    const getStudents = async () => {
        const result = await Fetch(studentsUrl, "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setStudentsCount(pagination.total);
            setStudents(items);
            setCurrentPage(page);
        } else {
            setStudents([]);
            setStudentsCount(0);
        }

        setGetWait(false);
    };

    const studentDetails = (id) => {
        setStudent(students.find((item) => item.id === id) || null);
        setPopup("details", "flex");
    };

    const filteringStudents = async () => {
        const result = await Fetch(studentsUrl, "GET", null);
        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setStudentsCount(pagination.total);
            setStudents(items);
            setCurrentPage(page);
            setPopup("filter", "none");
        }

        setFilterWait(false);
    };

    const deleteStudent = async () => {
        const result = await Fetch(`${host}/admin/users/${studentId}/delete`, "DELETE", null);

        if (result.status === 200) {
            setStudents((prev) => prev.filter((item) => item.id !== studentId));
            setStudentsCount((prev) => Math.max(Number(prev || 0) - 1, 0));
            setSnackBar("success", isAr ? "تم حذف الطالب بنجاح" : "Student deleted successfully");
            setPopup("delete", "none");
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getStudents();
        }
    }, [wait, page, order, search]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <AdminPageHero />

                <DashboardPanelCard
                    title={isAr ? "إدارة الطلاب" : "Students management"}
                    subtitle={isAr ? "استعرض الطلاب، ابحث بسرعة، وافتح التفاصيل أو احذف الحسابات عند الحاجة." : "Browse students, search quickly, and open details or delete accounts when needed."}
                    className="mx-0 !mt-0"
                >
                    <AdminListToolbar
                        isAr={isAr}
                        searchPlaceholder={intl.formatMessage({ id: "search_students" })}
                        search={search}
                        onSearchChange={setSearch}
                        order={order}
                        onOrderChange={setOrder}
                        totalLabel={intl.formatMessage({ id: "total_students" })}
                        totalCount={studentsCount}
                        onFilterClick={() => setPopup("filter", "flex")}
                        orderOptions={[
                            { value: "", label: intl.formatMessage({ id: "date" }) },
                            { value: "first_name", label: intl.formatMessage({ id: "student_name" }) },
                            { value: "email", label: intl.formatMessage({ id: "email" }) },
                        ]}
                    />

                    <TableContainer component={Paper} dir={language === "en" ? "ltr" : "rtl"} sx={{ boxShadow: "none", borderRadius: 3 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="students table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="full_name" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="email" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="education_specialization" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center"><FormattedMessage id="registeration_courses_count" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="registeration_date" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center"><FormattedMessage id="procedures" /></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {students.length > 0 ? students.map((item) => (
                                    <StyledTableRow
                                        key={item.id}
                                        onClick={() => studentDetails(item.id)}
                                        sx={{
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: theme.palette.mode === "dark" ? "action.hover" : "#f8fafc",
                                            },
                                        }}
                                    >
                                        <StyledTableCell align={language === "en" ? "left" : "right"} component="th" scope="row">
                                            {`${item.first_name || ""} ${item.last_name || ""}`.trim()}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>{item.email}</StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            {language === "en" ? item.major?.name_en : item.major?.name_ar}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center">
                                            {item.enrolled_courses_count ?? 0}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            {formatDate(item.verified_at)}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center">
                                            <Button
                                                variant="contained"
                                                className="h-8 !bg-red-300 !text-red-600 !font-bold hover:!bg-red-600 hover:!text-white"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setStudentId(item.id);
                                                    setPopup("delete", "flex");
                                                }}
                                            >
                                                <FormattedMessage id="delete" />
                                            </Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                {isAr ? "لا يوجد طلاب مطابقون للبحث أو التصفية الحالية." : "No students match the current search or filters."}
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }} dir="rtl">
                        <Button disabled={page + 1 === totalPages || totalPages === 0} onClick={() => setPage(currentPage + 1)}>
                            <NavigateNextIcon fontSize="large" />
                        </Button>
                        <Typography variant="body1" className="!text-xl" dir="ltr">
                            {totalPages ? currentPage + 1 : 0} / {totalPages || 0}
                        </Typography>
                        <Button disabled={page + 1 === 1} onClick={() => setPage(currentPage - 1)}>
                            <NavigateBeforeIcon fontSize="large" />
                        </Button>
                    </Box>
                </DashboardPanelCard>
            </PageContent>

            <ModalOverlay id="filter" onClose={() => setPopup("filter", "none")}>
                <StudentFilter
                    onClickConfirm={filteringStudents}
                    value={value}
                    setValue={setValue}
                    majorId={majorId}
                    setMajorId={setMajorId}
                    fromCount={fromCount}
                    setFromCount={setFromCount}
                    toCount={toCount}
                    setToCount={setToCount}
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    onClickClose={() => setPopup("filter", "none")}
                    filterWait={filterWait}
                    setFilterWait={setFilterWait}
                />
            </ModalOverlay>

            <ModalOverlay id="details" onClose={() => setPopup("details", "none")}>
                {student ? (
                    <StudentDetails
                        student={student}
                        onClickClose={() => setPopup("details", "none")}
                        setSnackBar={setSnackBar}
                        onUpdated={(updated) => {
                            setStudent(updated);
                            setStudents((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
                        }}
                    />
                ) : null}
            </ModalOverlay>

            <ModalOverlay id="delete" onClose={() => setPopup("delete", "none")}>
                <DeleteDialog
                    onClickConfirm={deleteStudent}
                    onClickCancel={() => setPopup("delete", "none")}
                    title={<FormattedMessage id="delete_title" />}
                    subtitle={<FormattedMessage id="delete_description" />}
                />
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Students;